/**
 * CustomMultiRowクラス.
 * (C)2024 Chubu Electric Power Grid Co.,Inc. All Rights Reserved.
 * version 1.00 NEC 2024/12/31 初版
 */
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import Typography from '@mui/material/Typography';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import * as wjgGrid from '@grapecity/wijmo.grid';
import {MultiRow, MultiRowCellGroup, MultiRowCell} from '@grapecity/wijmo.react.grid.multirow';
import {FlexGridFilter} from '@grapecity/wijmo.react.grid.filter';
import {CollectionView, hasClass, toggleClass, Point, DataType, Control, removeClass, addClass} from '@grapecity/wijmo';
import {Selector} from '@grapecity/wijmo.grid.selector';
import {CellMaker} from '@grapecity/wijmo.grid.cellmaker';

import {gridOperations} from '../../reducks/grid';
import AddIconButton from '../atoms/Buttons/AddIconButton.js';
import DeleteIconButton from '../atoms/Buttons/DeleteIconButton.js';
import {PositiveButton} from '../atoms/Buttons/PositiveButton.js';
import {isSmartPhone, formatStringDate, getDntIdType} from '../../common/common.js';
import {GRID_STATUS} from '../../common/commonConst.js';

/* usage
  // このファイルをimportする
  import CustomMultiRow from '../../../../molecules/CustomMultiRow.js';

  // gridのメソッドを操作するためにrefを作成
  constructor() {
    this.gridRef = React.createRef();
  }

  // render にCustomMultiRowを実装
  let props = {
      headersVisibility: "Column", // 行ヘッダ、列ヘッダの表示設定
                                   // "All" : 列ヘッダセルと行ヘッダセルの両方表示
                                   // "Column" : 列ヘッダセルのみ表示
                                   // "None" : 表示なし
                                   // "Row" : 行ヘッダセルのみ表示
      allowSorting: "SingleColumn", // 列ソート
                                    // "None" : ソートなし。デフォルト
                                    // "MultiColumn" : 一度に複数列ソート可
                                    // "SingleColumn" : 一度に1列ずつソート可
      frozenRows: 1, // 行固定(デフォルト0で固定なし)
      frozenColumns: 1, // 列固定(デフォルト0で固定なし)
      data: [], //一覧に表示するデータ
      initItems: {key1: value1, ...} // 項目と初期値を設定。
                                     // 行追加したときにinitItemsに設定した項目と初期値をグリッドに追加する。
                                     // 設定しないときはpropsに書かない。
      style: {maxHeight: "600px"}, // グリッドのスタイル設定
      rowHeaderType: "check", // 行ヘッダのスタイルを設定。
                              // "none" : なし
                              // "check" : チェックボックス有効
                              // "radio" : ラジオボタン有効。MultiRowはラジオボタンを使用しないこと。
                              // "edit" : 編集ダイアログ有効
      filterOn: true, // 列フィルター有効無効設定
      filters: ['id', 'xxx'], // filterOn=trueのとき、フィルターを有効にする列を設定
      isDisplayCount: false, // グリッドに設定したデータ件数表示有無を設定
      AddDeleteOn: true, // 行追加削除表示設定
      DeleteOn: true, // 行削除ボタンのみ表示設定
      ArrowButtonOn: true, // 行移動ボタンの表示設定。true:ボタン表示, false:ボタン非表示
      isReadOnly: true, // true:グリッド編集不可, false:グリッド編集可。未指定の場合はfalse
      trackChanges: true, // true:変更管理ON
      screenId: '10001', // 画面ID
      isRestore: true, // true:ローカルストレージに選択状態保存。デフォルトtrue
      maxRowSize: 100, // 追加可能行数。デフォルト100件。
      // 以下function。使用しない場合は書かない。
      editPopupItems: function // 編集ダイアログ処理
      refreshedFunction: function // グリッド更新時の処理
      loadedRowsFunction: function // 行がデータソースにバインドされた時に発生する処理
      formatItemFunction: function // セルを表す要素が作成されたときに発生する処理
      cellEditEndingFunction: function // セルの編集を終了したときに発生する処理
      cellEditEndedFunction: function // セルの編集を確定したときに発生する処理
      pastingCellFunction: function // クリップボードの内容をセルに貼り付けるときに発生する処理
      pastedCellFunction: function // クリップボードの内容をセルに貼り付けた後に発生する処理
      addItemFunction: function // 追加ボタンで行を追加したときの処理
      checkedFunction: function // チェックボックスがクリックされたときに発生する処理
      doShowMessage: function // this.props.doShowMessageを渡す
      filterChanging: function, // 列フィルターを編集する処理
      getError: function   // 項目の特定のプロパティに検証エラーが含まれているかどうかを判定する処理
    }
    <CustomMultiRow ref={this.gridRef} {...props}>
      // 子コンポーネントを記載
    </CustomMultiRow>

    // dataにIdを設定する。Idでデータを一意に判別できるようにする。
    let data = [{Id: orderno + rev等, orderno: "xxx", rev: "0"}]
    // renderの後でdataを設定する
    if (this.gridRef && this.gridRef.current) {
        this.gridRef.current.setItemsSource(data);
    }

  // MultiRowは行も列もドラッグできない
    参考: https://www.grapecity.co.jp/developer/wijmo
*/

const MAX_DNT_COUNT = 100;
const ROW_UNIQUE_KEY = 'Id';

/**
 * CustomMultiRowクラス
 */
class CustomMultiRow extends React.Component {
  constructor(props) {
    super(props);
    this.screenId = this.props.screenId ? this.props.screenId : '';
    this.selector = null;
    this.grid = null;
    this.selectedItems = [];
    this.filter = null;
    this.isRestore = !this.props.isRestore ? true : this.props.isRestore;
    this.frozenRows = 0;
    this.frozenColumns = 0;
    this.maxRowSize =
      this.props.maxRowSize != undefined || this.props.maxRowSize != null ?
      this.props.maxRowSize : MAX_DNT_COUNT;
    this.rowSpan = 2;
  }

  render() {
    const _frozenRows = this.props.frozenRows ? this.props.frozenRows : 0;
    const _frozenColumns = this.props.frozenColumns ?
      this.props.frozenColumns : 0;
    this.frozenRows = isSmartPhone() ? 0 : _frozenRows;
    this.frozenColumns = isSmartPhone() ? 0 : _frozenColumns;
    return this.renderGrid();
  }

  renderGrid() {
    const {isDisplayCount} = this.props;
    const _sourceCollection = this.getSourceCollection();
    let itemCount = 0;
    if (_sourceCollection) {
      itemCount = _sourceCollection.length;
    }
    return (
      <React.Fragment>
        {isDisplayCount &&
          <Typography variant="caption" display="block">
            検索結果：{itemCount} 件
          </Typography>
        }
        {/* multirowは行列のD&Dができないのでボタンで行移動する */}
        {this.props.ArrowButtonOn &&
        <div>
          <PositiveButton
            id="idMultiRowUp"
            onClick={() => this.doMove(1)}
            variant="contained"
            size="small"
            style={{minWidth: 20, marginRight: '2px'}}
            disabled={!!this.props.isReadOnly}>
            <ArrowUpwardIcon />
          </PositiveButton>
          <PositiveButton
            id="idMultiRowDown"
            onClick={() => this.doMove(-1)}
            variant="contained"
            size="small"
            style={{minWidth: 20}}
            disabled={!!this.props.isReadOnly}>
            <ArrowDownwardIcon />
          </PositiveButton>
        </div>}
        <MultiRow
          id="kyogaMulti"
          autoGenerateColumns={false}
          imeEnabled={true}
          initialized={this.initialized.bind(this)}
          deferResizing={false}
          showMarquee={true}
          alternatingRowStep={1}
          headersVisibility={this.props.headersVisibility ? this.props.headersVisibility : 'All'}
          // allowMerging="ColumnHeaders"
          allowSorting={this.props.allowSorting ? this.props.allowSorting : 'None'}
          selectionMode="MultiRange"
          style={this.props.style ? this.props.style : {height: 'auto'}}
          frozenRows={this.frozenRows}
          frozenColumns={this.frozenColumns}
          isReadOnly={!!this.props.isReadOnly}
          collapsedHeaders={null}
          validateEdits={
            typeof this.props.validateEdits !== 'undefined' ? this.props.validateEdits : true}
          bigCheckboxes={true}
        >
          {(this.props.AddDeleteOn || this.props.DeleteOn) &&
            <MultiRowCellGroup align="center" cssClassAll="borderOff">
              <MultiRowCell binding='edit' header=' ' width={60}/>
            </MultiRowCellGroup>
          }
          {this.props.children}
          {this.props.filterOn &&
            <FlexGridFilter initialized={this.initializedFilter.bind(this)}/>}
        </MultiRow>
        <DeleteIconButton isDisabled={!!this.props.isReadOnly}/>
        <AddIconButton isDisabled={!!this.props.isReadOnly}/>
      </React.Fragment>
    );
  }

  /**
   * 矢印ボタン押下
   * @param {number} dest 1:上、-1:下
   */
  doMove = (dest) => {
    if (!this.grid) {
      return;
    }
    const selectedRows = this.grid.selectedRows;
    if (!selectedRows || selectedRows.length == 0) {
      return;
    }

    // セル移動前に現在の位置を保持する
    const range = this.grid.selection;

    const collectionView = this.grid.collectionView;
    if (dest > 0) {
      this.grid.itemsSource.deferUpdate(function() {
        if (0 <= selectedRows[0].dataIndex - 1) {
          collectionView.sourceCollection.splice(
              selectedRows[0].dataIndex, 1); // 選択した行をcollectionViewから削除
          collectionView.sourceCollection.splice(
              selectedRows[0].dataIndex - 1, 0,
              selectedRows[0].dataItem); // 一個上に選択した行を挿入
          collectionView.refresh();
        }
      });
    }
    if (dest < 0) {
      this.grid.itemsSource.deferUpdate(function() {
        if (collectionView.itemCount > selectedRows[0].dataIndex + 1) {
          collectionView.sourceCollection.splice(
              selectedRows[0].dataIndex, 1); // 選択した行をcollectionViewから削除
          collectionView.sourceCollection.splice(
              selectedRows[0].dataIndex + 1, 0,
              selectedRows[0].dataItem); // 一個上に選択した行を挿入
          collectionView.refresh();
        }
      });
    }

    if (range !== undefined && range !== null) {
      const currentCol = range.col;
      const newRow = range.row - (dest * this.rowSpan);
      const rowSize = (collectionView?.itemCount * this.rowSpan) - 1; // 行は0始まり
      if (newRow < 0 || rowSize < newRow) {
        return;
      }
      this.grid.select(newRow, currentCol);
    }
  };

  showEditIcon(s, e) {
    // 行ヘッダに編集アイコンを表示
    if (e.panel === s.rowHeaders &&
        e.col === (s.rowHeaders.columns.length - 1)) {
      if (s.rowHeaders.rows[e.row].isReadOnly) {
        e.cell.innerHTML = '<div></div>';
      } else {
        e.cell.innerHTML = '<div class="bf-centerPos"><span id="popupEdit" class="wj-glyph-pencil"></span></div>';
      }
    }
  }

  setCheckBox(grid) {
    // 行ヘッダにチェックボックスを表示
    this.selector = new Selector(grid, {
      itemChecked: (s, e) => {
        let _selectedItems = grid.rows.filter((r) => r.isSelected);
        // MultiRowは2行同じデータが返ってくるので、重複を除く
        this.selectedItems = [];
        for (const _selectedItem of _selectedItems) {
          if (this.selectedItems) {
            if (this.selectedItems.filter(
                (data) =>
                  Object.hasOwn(data.dataItem, ROW_UNIQUE_KEY) &&
                  Object.hasOwn(_selectedItem.dataItem, ROW_UNIQUE_KEY) &&
                    data.dataItem[ROW_UNIQUE_KEY] ==
                   _selectedItem.dataItem[ROW_UNIQUE_KEY]).length > 0) {
              continue;
            }
            this.selectedItems.push(_selectedItem);
          }
        }
        // チェックボックスクリック時イベントがあれば実行
        const checkedFunction = this.props.checkedFunction;
        if (checkedFunction) {
          checkedFunction();
        }
      },
    });
    this.selector.column.grid.headersVisibility = wjgGrid.HeadersVisibility.All;
    this.selector.column =
      this.selector.column.grid.rowHeaders.columns.length == 2 ?
      this.selector.column.grid.rowHeaders.columns[1] :
      this.selector.column.grid.rowHeaders.columns[0];
    this.selectedItems = [];
  }

  initialized(grid) {
    this.grid = grid;
    const rowHeaderType = this.props.rowHeaderType;
    const AddDeleteOn = this.props.AddDeleteOn;

    if (this.props.data) {
      this.grid.itemsSource = createCollectionView(
          this.props.data, !!this.props.trackChanges,
          this.props.initItems, this.props.getError);
    }

    // editの場合、行ヘッダーを2行にする
    if (rowHeaderType == 'edit') {
      grid.rowHeaders.columns.push(new wjgGrid.Column());
      grid.mergeManager = new CustomMergeManager(grid);
    }
    if (grid.rowHeaders.columns.length == 2) {
      grid.rowHeaders.columns[0].width = 15;
    }


    // 行ヘッダー設定(チェックボックス)
    if (rowHeaderType === 'check') {
      this.setCheckBox(grid);
    }

    // 行追加、削除ボタン設定
    if (AddDeleteOn) {
      // add a footer row
      const footerRow = new wjgGrid.GroupRow();
      footerRow.height = 50;
      grid.columnFooters.rows.push(footerRow);
    }

    // セルの書式設定を行う
    let _formatItemHandler = this.formatItemHandler.bind(this);
    grid.formatItem.addHandler(_formatItemHandler);

    const _resizingColumnHandler = this.resizingColumnHandler.bind(this);
    grid.resizingColumn.addHandler(_resizingColumnHandler);

    // コントロールが内容を更新した後で発生する処理
    const _refreshedHandler = this.refreshedHandler.bind(this);
    grid.refreshed.addHandler(_refreshedHandler);

    // グリッド行がデータソースの項目に連結された後に発生する処理
    const _loadedRowsHandler = this.loadedRowsHandler.bind(this);
    grid.loadedRows.addHandler(_loadedRowsHandler);

    const _beginningEditHandler = this.beginningEditHandler.bind(this);
    grid.beginningEdit.addHandler(_beginningEditHandler);

    // セル編集終了時の処理
    const _cellEditEndingHandler = this.cellEditEndingHandler.bind(this);
    grid.cellEditEnding.addHandler(_cellEditEndingHandler);

    // セル編集確定時の処理
    const _cellEditEndedHandler = this.cellEditEndedHandler.bind(this);
    grid.cellEditEnded.addHandler(_cellEditEndedHandler);

    // セルペースト時の処理
    const _pastingCellHandler = this.pastingCellHandler.bind(this);
    grid.pastingCell.addHandler(_pastingCellHandler);

    // セルペースト後の処理
    const _pastedCellHandler = this.pastedCellHandler.bind(this);
    grid.pastedCell.addHandler(_pastedCellHandler);

    const _hostElementClickEvent = this.hostElementClickEvent.bind(this);
    grid.hostElement.addEventListener('click', _hostElementClickEvent);
  }

  formatItemHandler(s, e) {
    const rowHeaderType = this.props.rowHeaderType;
    // ヘッダーのみ折り返す
    toggleClass(e.cell, 'wj-wrap', s.columnHeaders === e.panel);

    if (s.columnHeaders === e.panel) {
      // 列ヘッダーに\nがあれば<br/>に置換する
      if (e.cell.innerHTML &&
        e.cell.innerHTML.indexOf('\\n') != -1) {
        e.cell.innerHTML = e.cell.innerHTML.replace(/\\n/g, '<br/>');
      }
      if (e.cell.innerHTML &&
        e.cell.innerHTML.indexOf('\n') != -1) {
        e.cell.innerHTML = e.cell.innerHTML.replace(/\n/g, '<br/>');
      }
      // ヘッダーのボーダーの色を黒にする
      if (hasClass(e.cell, 'borderOff')) {
        e.cell.style.borderBottom = 'solid 1px #000';
      }
    }

    if (this.props.AddDeleteOn) {
      this.setAddDeleteButton(s, e);
    } else if (this.props.DeleteOn) {
      this.setDeleteButton(s, e);
    }

    // 行ヘッダー設定
    if (rowHeaderType === 'edit') {
      this.showEditIcon(s, e);
    }
    // セルの書式設定
    if (this.props.formatItemFunction) {
      this.props.formatItemFunction(s, e, this.grid);
    }
  }

  setAddDeleteButton(s, e) {
    const col = s.columns[e.col];
    this.setDeleteButton(s, e);
    // add footer button
    if (e.panel === s.columnFooters && col.binding === 'edit' &&
          document.getElementById('btnTblFooter')) {
      e.cell.innerHTML = document.getElementById('btnTblFooter').innerHTML; // AddIconButtonのid
    }
  }

  setDeleteButton(s, e) {
    const col = s.columns[e.col];
    // add edit button
    if (e.panel === s.cells) {
      const item = s.rows[e.row].dataItem;
      if (col.binding === 'edit') {
        e.cell.innerHTML = document.getElementById('btnMode').innerHTML; // DeleteIconButtonのid
        if (s.rows[e.row].isReadOnly) {
          e.cell.querySelector('#iconDeleteRow').setAttribute('disabled', true);
        }
        e.cell['dataItem'] = item;
      }
    }
  }

  resizingColumnHandler(s, e) {
    // ヘッダーのみ、列幅変更時に高さを変更し、列名を見えるようにする
    for (let i = 0; i < this.grid.columnHeaders.rows.length; i++) {
      if (this.grid.columnHeaders.getCellElement(i, e.col)) {
        const height =
        this.grid.columnHeaders.getCellElement(i, e.col).scrollHeight;
        if (height > this.grid.columnHeaders.rows.defaultSize) {
          this.grid.autoSizeRow(i, true);
        }
      }
    }
  }

  refreshedHandler(s, e) {
    if (this.props.refreshedFunction) {
      this.props.refreshedFunction(s, e);
    }
  }

  loadedRowsHandler(s, e) {
    if (s.rows && s.rows.length > 0 && this.isRestore) {
      this.restoreSelectedItems(this.props.rowHeaderType);
      this.restoreScrollPosition();
      if (this.props.rowHeaderType === 'check') {
        // チェックボックスの選択状態を復元
        this.onItemChecked();
      }
    }
    if (this.props.loadedRowsFunction) {
      this.props.loadedRowsFunction(s, e);
    }
  }

  beginningEditHandler(s, e) {
    this.cellEditCancel(e);
  }

  cellEditEndingHandler(grid, event) {
    if (this.props.cellEditEndingFunction) {
      this.props.cellEditEndingFunction(grid, event);
    }
  }

  cellEditEndedHandler(grid, event) {
    if (this.props.cellEditEndedFunction) {
      this.props.cellEditEndedFunction(grid, event);
    }
  }

  pastedCellHandler(grid, event) {
    if (this.props.pastedCellFunction) {
      this.props.pastedCellFunction(grid, event);
    }
  }

  pastingCellHandler(grid, event) {
    if (this.cellEditCancel(event)) {
      return;
    }
    if (this.props.pastingCellFunction) {
      this.props.pastingCellFunction(grid, event);
    }
  }

  cellEditCancel(event) {
    const element = event.panel.getCellElement(event.row, event.col);
    // 'wj-state-disabled'が設定してあるセルは編集不可
    if (element !== undefined && element !== null &&
        element.className?.indexOf('wj-state-disabled') != -1) {
      event.cancel = true;
    }
    return event.cancel;
  }

  async hostElementClickEvent(e) {
    let key = e.target.id;
    if (e.target.nodeName == 'path') {
      // material UI のSVG IconはnodeNameがpathになる
      key = e.target.parentNode.id;
    }
    const cv = this.grid.collectionView;
    if (!cv?.sourceCollection) {
      return;
    }
    switch (key) {
      case 'btnDeleteRow': {
        const ht = this.grid.hitTest(e);
        if (ht) {
          let index = this.calcIndex(ht);
          cv.beginUpdate();
          cv.removeAt(index);
          cv.endUpdate();
        }
        break;
      }
      case 'btnAddRow': {
        this.btnAddRowClickEvent(e, cv);
        break;
      }
      case 'popupEdit': {
        if (this.props.editPopupItems) {
          this.props.editPopupItems(this.grid, e);
        }
        break;
      }
      default:
        break;
    }
  }

  btnAddRowClickEvent(e, cv) {
    const _itemsCount = cv.sourceCollection.length;
    if (_itemsCount >= this.maxRowSize) {
      if (this.props.doShowMessage) {
        this.props.doShowMessage({
          message: {
            id: 'BFY000053E',
            values: [this.maxRowSize],
          },
        });
      }
      return;
    }
    cv.beginUpdate();
    const _newItem = cv.addNew();
    const _nextRowId = this.getNextRowId(cv.sourceCollection);
    const _nextBukkenNo = this.getNextBukkenNo(cv.sourceCollection);
    if (_newItem) {
      _newItem['rowId'] = _nextRowId;
      _newItem['gridStatus'] = GRID_STATUS.INSERT;
      if (Object.hasOwn(_newItem, 'bukkenNoKeikan') && _nextBukkenNo > 0) {
        _newItem['bukkenNoKeikan'] = _nextBukkenNo;
      }
      if (this.props.addItemFunction) {
        this.props.addItemFunction(e, cv, _newItem);
      }
      cv.commitNew();
    }
    cv.endUpdate();
  }

  getNextRowId(sourceCollection) {
    let maxRowId = 0;
    if (sourceCollection.length > 0) {
      maxRowId = sourceCollection.reduce((src, dest) => {
        return src.rowId > dest.rowId ? src : dest;
      }).rowId;
    }
    return maxRowId + 1;
  }

  getNextBukkenNo(sourceCollection) {
    let _maxBukkenNo = 1;
    if (sourceCollection == null || sourceCollection.length == 0) {
      return _maxBukkenNo;
    }
    const hasBukkenNo = Object.hasOwn(sourceCollection[0], 'bukkenNoKeikan');
    if (!hasBukkenNo) {
      return _maxBukkenNo;
    }
    if (hasBukkenNo && sourceCollection.length > 0) {
      _maxBukkenNo = sourceCollection.reduce((src, dest) => {
        return src.bukkenNoKeikan > dest.bukkenNoKeikan ? src : dest;
      }).bukkenNoKeikan;
    }
    return _maxBukkenNo == null ? 1 : _maxBukkenNo + 1;
  }

  calcIndex(ht) {
    let index = 0;
    if (ht.row > 1) {
      index = ht.row % 2 == 0 ? ht.row / 2 : (ht.row - 1) / 2;
    }
    return index;
  }

  initializedFilter(filter) {
    this.filter = filter;
    this.filter.filterColumns = this.props.filters;
    this.filter.showSortButtons = false;
    const filterChanging = this.props.filterChanging;

    this.filter.filterChanging.addHandler((s, e) => {
      let edt = s.activeEditor;
      let lbHost = edt.hostElement.querySelector('[wj-part=div-values]');
      let lb = Control.getControl(lbHost);
      if (e.getColumn().dataType == DataType.Boolean) {
        lb.itemFormatter = (index) => {
          let result = '(なし)';
          if (lb.collectionView.items[index].value === true) {
            result = 'ON';
          } else if (lb.collectionView.items[index].value === false) {
            result = 'OFF';
          }
          return result;
        };
      }

      if (filterChanging) {
        filterChanging(s, e);
      }
    });
  }

  // チェックボックスまたはラジオボタンで選択した行データを返す
  selectedItem() {
    return this.selectedItems != null ? this.selectedItems : [];
  }

  /**
   * 選択行の件数を返す
   * @return {number}
   */
  selectedItemCount() {
    return this.selectedItems != null ? this.selectedItems.length : 0;
  }

  /**
   * チェックボックスチェンジイベント発行
   */
  onItemChecked() {
    if (this.selector) {
      this.selector.onItemChecked();
    }
  }

  /**
   * バインドしたデータのisSelectedを設定
   * @param {bool} selected true:選択、false:未選択
   */
  setAllSelected(selected) {
    if (this.grid != null && this.grid.rows.length > 0) {
      for (const row of this.grid.rows) {
        row.isSelected = selected;
      }
    }
  }

  /**
   * ラジオボタン、チェックボックスの選択状態復元
   * @param {string} rowHeaderType ヘッダーに表示する選択項目名
   */
  restoreSelectedItems(rowHeaderType) {
    const _screenId = this.screenId + '_grid';
    const selection = this.props.selections.filter(
        (data) => data.screenId == _screenId);
    let isEmptySelection = function(grid, selection) {
      return (grid == null ||
              grid.rows == null ||
              grid.rows.length == 0 ||
              selection.length == 0 ||
              !selection[0].compareKey ||
              selection[0].selectedItems == null ||
              selection[0].selectedItems.length == 0);
    };
    if (isEmptySelection(this.grid, selection)) {
      return;
    }
    // キーが一致するデータを選択する
    const compareKey = selection[0].compareKey;
    const selectedItems = selection[0].selectedItems ?
        selection[0].selectedItems : [];
    if (!Object.hasOwn(selectedItems[0], compareKey) ||
        !Object.hasOwn(this.grid.rows[0].dataItem, compareKey)) {
      return;
    }
    for (const selectedItem of selectedItems) {
      const sameRows = this.grid.rows.filter(
          (row) => row.dataItem[compareKey] == selectedItem[compareKey]);
      if (rowHeaderType === 'check') {
        // multirowは同じものが複数行あるので、全てチェックする
        // そうしないと行の一部が未選択になってしまう
        for (const sameRow of sameRows) {
          sameRow.isSelected = true;
        }
      }
    }
  }

  /**
   * スクロール位置復元
   */
  restoreScrollPosition() {
    if (this.screenId) {
      if (this.grid == null || this.grid.rows == null ||
          this.grid.rows.length == 0) {
        return;
      }
      const _screenId = this.screenId + '_grid';
      const dataList = this.props.scrollPosition.filter(
          (data) => data.screenId == _screenId);
      if (dataList.length == 0 ||
        dataList[0].scrollPosition == null) {
        return;
      }
      // TODO y軸が復元できない。値を設定しても0になる
      this.grid.scrollPosition = new Point(
          dataList[0].scrollPosition.x, dataList[0].scrollPosition.y);
      this.grid.refresh(false);
    }
  }

  /**
   * グリッドを返す
   * @return {object} グリッド本体
   */
  getGrid() {
    return this.grid;
  }

  /**
   * CollectionViewのデータを返す
   * @return {object} 基になる（フィルタリングもソートもされていない）データ
   */
  getSourceCollection() {
    return this.grid?.collectionView?.sourceCollection;
  }

  /**
   * CollectionViewのデータを返す
   * @return {object} フィルタリング、ソート、グループ化が適用されたデータ
   */
  getItems() {
    return this.grid?.collectionView?.items ?
      this.grid?.collectionView?.items : [];
  }

  /**
   * 追加、変更、削除したデータを返す
   * 注意)CollectionView メソッド（editItem /commitEdit、 addNew /commitNew、remove）を使用しないと
   *     同じ行を追加、削除したとき、 itemsAddedとitemsRemovedにそれぞれデータが入る。
   *     同じ行を複数回変更したとき、変更した回数分itemsEditedにデータが入る。
   * @return {array} 追加、変更、削除したデータ
   */
  itemChangedList() {
    let _items = [];
    if (!this.grid.collectionView) {
      return _items;
    }
    for (const item of this.grid.collectionView.itemsAdded) {
      item['gridStatus'] = GRID_STATUS.INSERT;
      _items.push(item);
    }
    for (const item of this.grid.collectionView.itemsEdited) {
      if (item['gridStatus'] === null || item['gridStatus'] === '') {
        item['gridStatus'] = GRID_STATUS.UPDATE;
      }
      _items.push(item);
    }
    for (const item of this.grid.collectionView.itemsRemoved) {
      if (item['gridStatus'] === null || item['gridStatus'] === '') {
        item['gridStatus'] = GRID_STATUS.DELETE;
      }
      _items.push(item);
    }
    return _items;
  }

  /**
   * 追加、変更、削除したデータを含む全てのデータを返す
   * @return {array} 状態（変更なし、追加、変更、削除）を設定したデータ
   */
  getAllItemsWithState() {
    const changedList = this.itemChangedList();
    let sc = this.getSourceCollection();
    const deleteItems =
        changedList.filter((data) => data.gridStatus == GRID_STATUS.DELETE);
    return sc.concat(deleteItems);
  }

  /**
   * 編集した内容をコミットする
   */
  commit() {
    const cv = this.grid?.collectionView;
    if (cv?.trackChanges) {
      cv.commitEdit();
    }
  }

  /**
   * データを追加、変更、削除したかチェックする
   * @return {bool} true:変更あり, false:変更なし
   */
  isItemsModified() {
    let _isModified = false;
    if (this.grid?.collectionView) {
      _isModified = this.grid.collectionView.itemsAdded.length > 0 ||
                  this.grid.collectionView.itemsEdited.length > 0 ||
                  this.grid.collectionView.itemsRemoved.length > 0;
    }
    return _isModified;
  }

  /**
   * 編集した内容をコミットし、データが変更されているかチェックする
   * グリッドが読み取り専用の場合はfalseを返す
   * @return {bool} true:変更あり, false:変更なし
   */
  commitAndCheck() {
    let _isModified = false;
    if (this.props.isReadOnly === false) {
      this.commit();
      _isModified = this.isItemsModified();
    }
    return _isModified;
  }

  /**
   * グリッドのデータをDBに登録した後呼び出す。
   * clearChangesを実行すると
   * itemsAdded 、itemsRemoved 、itemsEdited
   * の全ての変更をクリアする。
   */
  clearChanges() {
    if (this.grid?.collectionView) {
      this.grid.collectionView.clearChanges();
    }
  }

  /**
   * 画面の情報を設定する
   * @param {string} _screenId 画面Id
   * @param {bool} restore true:画面の状態を復元する、false:復元しない
   */
  setScreenState(_screenId, restore=true) {
    this.isRestore = restore;
    this.screenId = _screenId;
  }

  /**
   * 以下の状態を保存する
   * ・ラジオボタンまたはチェックボックスで選択したデータ
   * ・スクロール位置
   * @param {string} screenId 画面Id
   * @param {array} compareKey
   *                  一覧のデータと保存した選択データを比較して同じ行か判別するためのキー
   *                  selectedItemsに存在するキーを1つ以上設定する
   * @param {bool} isClear true: ストアに保存した状態をクリアする
   */
  saveScreenState(screenId, compareKey, isClear=false) {
    this.saveSelectedItems(screenId, compareKey, isClear);
    this.saveScrollPosition(screenId, isClear);
  }

  /**
     * ラジオボタンまたはチェックボックスで選択したデータを保存する
     * @param {string} screenId 画面Id
     * @param {array} compareKey
     *                  一覧のデータと保存した選択データを比較して同じ行か判別するためのキー
     *                  selectedItemsに存在するキーを1つ以上設定する
     * @param {bool} isClear true: ストアに保存した選択行をクリアする
     */
  saveSelectedItems(screenId, compareKey, isClear=false) {
    const _screenId = this.screenId + '_grid';
    if (!isClear) {
      let _dataItems = [];
      for (const selectedItem of this.selectedItems) {
        _dataItems.push(selectedItem.dataItem);
      }
      this.props.doSaveSelectedItems(
          _screenId, compareKey, _dataItems);
    } else {
      this.selectedItems = [];
      this.props.doSaveSelectedItems(
          _screenId, compareKey, null);
    }
  }

  /**
     * スクロール位置を保存する
     * @param {string} screenId 画面Id
     * @param {bool} isClear true: ストアに保存した状態をクリアする
     */
  saveScrollPosition(screenId, isClear=false) {
    if (this.grid == null) {
      return;
    }
    const _screenId = this.screenId + '_grid';
    if (!isClear) {
      const _scrollPosition = this.grid.scrollPosition;
      this.props.doSaveScrollPosition(
          _screenId, _scrollPosition);
    } else {
      this.props.doSaveScrollPosition(
          _screenId, null);
    }
  }

  /**
   * セルの選択範囲を設定
   * @param {number} row
   * @param {number} col
   */
  setCellRange(row, col) {
    if (this.grid) {
      this.grid.selection = new wjgGrid.CellRange(row, col);
    }
  }

  /**
   * itemsSourceにCollectionView設定
   * @param {array} items
   */
  setItemsSource(items) {
    const {trackChanges, initItems, getError} = this.props;
    const self = this;
    let setItemSource = function(grid) {
      const _columns = grid.columns;
      const dateCols = _columns.filter((col) =>
        col.dataType == DataType.Date);
      const _tmpItems = items || [];
      let orgItems = Array.isArray(_tmpItems) ? _tmpItems : [_tmpItems];
      let _items = orgItems.map((item) => {
        let _tmp = {...item};
        for (const col of dateCols) {
          if (item && col.binding && Object.hasOwn(item, col.binding) &&
          !col.format) {
            _tmp[col.binding] = formatStringDate(item[col.binding]);
          }
        }
        return _tmp;
      });
      const cv = createCollectionView(
          _items, trackChanges, initItems, getError);
      grid.itemsSource = cv;
    };
    if (self.getGrid()) {
      setItemSource(self.getGrid());
      return;
    }
    let _counter = 0;
    const intervalId = setInterval(() => {
      _counter++;
      if (!self.getGrid()) {
        return;
      }
      if (_counter > 1000) {
        clearInterval(intervalId);
        return;
      }
      clearInterval(intervalId);
      setItemSource(self.getGrid());
    }, 1);
  }

  /**
   * 選択行に電柱情報を反映する
   * @param {object} editItem 選択行
   * @param {object} dnt 電柱選択画面の戻り値
   * @param {string} number 1 or 2
   * @param {object} bindingAlias 電柱選択戻り値の項目を画面側の項目名に読み替える。
   *                              電柱情報を設定する前に編集対象データにbindingAliasの項目があればnullを設定する。
   */
  updateDnt(editItem, dnt, number, bindingAlias={}) {
    if (!editItem || !dnt) {
      return;
    }
    const cv = this.grid.collectionView;
    cv.beginUpdate();
    cv.editItem(editItem);

    for (const dntKey of Object.keys(bindingAlias)) {
      const itemKey = bindingAlias[dntKey];
      // 前の情報をクリア
      if (Object.hasOwn(editItem, itemKey)) {
        editItem[itemKey] = null;
      }
      // 電柱の値を編集対象に設定
      if (Object.hasOwn(dnt, dntKey)) {
        editItem[itemKey] = dnt[dntKey];
      }
    }

    this.setDntIdType(editItem, dnt, number);

    console.debug(editItem);

    cv.commitEdit();
    cv.endUpdate();
  }

  setDntIdType(target, dnt, number) {
    const key = 'idType' + number;
    if (!Object.hasOwn(target, key)) {
      return;
    }

    target[key] = getDntIdType(dnt);
  }
}

/**
 * CollectionView生成
 * @param {array} items
 * @param {bool} trackChanges
 * @param {object} initItems
 * @param {func} getError
 * @return {object} CollectionView
 */
export function createCollectionView(
    items, trackChanges=true, initItems={}, getError=null) {
  const _list = [];
  const _items = items || [];
  const _initItems = initItems || {};
  let _index = 1;
  for (const item of _items) {
    let data = {};
    Object.assign(data, item);
    data['rowId'] = _index++; // 内部管理用
    _list.push(data);
  }
  const cv = new CollectionView(
      _list,
      {
        trackChanges: trackChanges, // 変更管理ON
        newItemCreator: function() {
          // 項目は新しく作成すること
          // 同じ変数を返すとGridが同じ行とみなし、同じデータが入ってしまう
          const newItems = {};
          for (const key in _initItems) {
            if (Object.hasOwn(_initItems, key)) {
              newItems[key] = _initItems[key];
            }
          }
          return newItems;
        },
        getError: getError,
      },
  );
  return cv;
}

/**
   * セルのボタンを非活性にする
   * @param {object} element
   */
export function disabledCellButton(element) {
  removeClass(element, 'bf-btn-navy');
  addClass(element, 'wj-state-disabled');
};

/**
   * セルのボタンを活性にする
   * @param {object} element
   */
export function unDisabledCellButton(element) {
  removeClass(element, 'wj-state-disabled');
  addClass(element, 'bf-btn-navy');
};


/**
 * GridColumn生成
 * @param {object} gridItems
 * @param {bool} isDisabled
 * @return {array} component
 */
export function createComponents(gridItems, isDisabled) {
  let component = [];
  if (!gridItems) {
    return component;
  }
  const keys = Object.keys(gridItems);
  let _currentHeader = gridItems[keys[0]].groupHeader;
  let _flex = gridItems[keys[0]].flex;
  let _currentColumns = [];
  for (const key of Object.keys(gridItems)) {
    if (gridItems[key].header == '') {
      continue;
    }
    if (_currentColumns.length > 0 &&
        (_currentHeader != gridItems[key].groupHeader || _currentHeader == '')) {
      let _span = Math.ceil(_currentColumns.length / 2);
      if (_flex) {
        _span = _currentColumns.length;
      }
      component.push(<MultiRowCellGroup header={_currentHeader} align="center" colspan={_span}>
        {_currentColumns}
      </MultiRowCellGroup>);
      _currentHeader = gridItems[key].groupHeader;
      _flex = gridItems[key].flex;
      _currentColumns = [];
    }
    setColumn(gridItems[key], _currentColumns, isDisabled);
  }

  if (_currentColumns.length > 0) {
    const _span = Math.ceil(_currentColumns.length / 2);
    component.push(<MultiRowCellGroup header={_currentHeader} align="center" colspan={_span}>
      {_currentColumns}
    </MultiRowCellGroup>);
  }
  return component;
}

function setColumn(gridItem, currentColumns, isDisabled) {
  if (gridItem.visible === false) {
    // do nothing
  } else if (gridItem.dataType == 'cellMaker') {
    currentColumns.push(<MultiRowCell binding={gridItem.binding}
      header={gridItem.header} width={gridItem.width}
      cellTemplate={CellMaker.makeButton({
        text: gridItem.text,
        cssClass: isDisabled ? 'wj-state-disabled' : 'bf-btn-navy',
        click: gridItem.click,
        attributes: {
          id: 'btn' + gridItem.binding,
          tabindex: -1,
        },
      })}
    />);
  } else if (gridItem.dataType == 'link') {
    currentColumns.push(<MultiRowCell binding={gridItem.binding}
      header={gridItem.header} width={gridItem.width}
      visible={gridItem.visible}
      cellTemplate= {CellMaker.makeLink({
        click: gridItem.click,
        attributes: {
          id: 'link' + gridItem.binding,
          tabindex: -1,
        },
      })}
      dataType={String}
      cssClass={gridItem.cssClass}
    />);
  } else if (gridItem.dataType == 'Number') {
    currentColumns.push(<MultiRowCell
      binding={gridItem.binding} header={gridItem.header}
      dataType={gridItem.dataType} width={gridItem.width}
      dataMap={null}
      format={gridItem.format}
      isReadOnly={gridItem.isReadOnly} isRequired={false}/>);
  } else if (gridItem.dataType == 'Boolean') {
    currentColumns.push(<MultiRowCell
      binding={gridItem.binding} header={gridItem.header}
      dataType={gridItem.dataType} width={gridItem.width}
      dataMap={null}
      isReadOnly={gridItem.isReadOnly}
    />);
  } else if (gridItem.dataType == 'Date') {
    currentColumns.push(<MultiRowCell
      binding={gridItem.binding} header={gridItem.header}
      format={gridItem.format}
      editor={gridItem.editor} width={gridItem.width}
      isReadOnly={gridItem.isReadOnly} isRequired={false}
      visible={gridItem.visible}
      dataMap={null}
      cssClass='bf-rightPos'
    />);
  } else if (gridItem.dataType == 'String') {
    if ('dataMap' in gridItem) {
      currentColumns.push(<MultiRowCell
        binding={gridItem.binding} header={gridItem.header}
        dataType={gridItem.dataType} width={gridItem.width}
        isReadOnly={gridItem.isReadOnly}
        dataMap={gridItem.dataMap}/>);
    } else {
      const _multiLine = 'multiLine' in gridItem ? gridItem.multiLine : false;
      const _cssClass = 'cssClass' in gridItem ? gridItem.cssClass : 'bf-leftPos';
      currentColumns.push(<MultiRowCell
        binding={gridItem.binding} header={gridItem.header}
        dataType={gridItem.dataType} width={gridItem.width}
        isReadOnly={gridItem.isReadOnly}
        dataMap={null}
        multiLine={_multiLine}
        cssClass={_cssClass}
      />);
    }
  }
}

CustomMultiRow.propTypes = {
  AddDeleteOn: PropTypes.bool,
  DeleteOn: PropTypes.bool,
  allowSorting: PropTypes.string,
  checkedFunction: PropTypes.func,
  children: PropTypes.array,
  isDisplayCount: PropTypes.bool,
  ArrowButtonOn: PropTypes.bool,
  data: PropTypes.array,
  editPopupItems: PropTypes.func,
  filterOn: PropTypes.bool,
  formatItemFunction: PropTypes.func,
  frozenRows: PropTypes.number,
  frozenColumns: PropTypes.number,
  maxRowSize: PropTypes.number,
  headersVisibility: PropTypes.string,
  refreshedFunction: PropTypes.func,
  rowHeaderType: PropTypes.string,
  style: PropTypes.object,
  isReadOnly: PropTypes.bool,
  initItems: PropTypes.object,
  filters: PropTypes.array,
  validateEdits: PropTypes.bool,
  selections: PropTypes.array,
  scrollPosition: PropTypes.array,
  trackChanges: PropTypes.bool,
  doShowMessage: PropTypes.func,
  getError: PropTypes.func,
  filterChanging: PropTypes.func,
  doSaveSelectedItems: PropTypes.func,
  doSaveScrollPosition: PropTypes.func,
  screenId: PropTypes.string,
  isRestore: PropTypes.bool,
  cellEditEndedFunction: PropTypes.func,
  cellEditEndingFunction: PropTypes.func,
  pastingCellFunction: PropTypes.func,
  pastedCellFunction: PropTypes.func,
  addItemFunction: PropTypes.func,
  loadedRowsFunction: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    selections: state.grid.selections != null ? state.grid.selections : [],
    scrollPosition:
      state.grid.scrollPosition != null ? state.grid.scrollPosition : [],
  };
};

const mapDispatchToProps = {
  doSaveSelectedItems: gridOperations.doSaveSelectedItems,
  doSaveScrollPosition: gridOperations.doSaveScrollPosition,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    {forwardRef: true},
)(CustomMultiRow);

class CustomMergeManager extends wjgGrid.MergeManager {
  _baseMergeManager;

  constructor(grid) {
    super();
    // save instance of old merge manager to provide default behavior whenever required
    this._baseMergeManager = grid.mergeManager;
  }

  getMergedRange(panel, r, c, clip = true) {
    if (
      panel.cellType == wjgGrid.CellType.RowHeader ||
      panel.cellType == wjgGrid.CellType.TopLeft
    ) {
      // create basic cell range
      let rng = new wjgGrid.CellRange(r, c);
      // get old merged range
      let rg = this._baseMergeManager.getMergedRange(panel, r, c, clip);
      // update new range to restrict the columns merging
      rng.row = rg.row;
      rng.row2 = rg.row2;
      rng.col = c;
      rng.col2 = c;

      return rng;
    }
    // return default merge range for other cellType
    return this._baseMergeManager.getMergedRange(panel, r, c, clip);
  }
}
